var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './DetectedApplicationCard.scss';
import { AppDisplay } from '@ui/applications';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { DetectedApplicationActions } from './DetectedApplicationActions';
import { FullUserBoundary } from '@ui/general';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { SonarText } from '@ui/text';
import { Col, Row, Tooltip } from 'antd';
import { COPY_SPECIFIC, DATA_SECURITY_DASHBOARD } from '@sonar-software/sonar-ts-constants';
import { DataSecurityApplicationStatus, FeatureFlag, getDataSecurityDetectedApplicationById } from '@sonar-software/sonar-core';
import { Else, If, Then, When } from 'react-if';
import { EyeInvisibleOutlined, WarningTwoTone } from '@ant-design/icons';
import React, { useCallback, useState } from 'react';
import { useFeatureFlag, useOnMount } from '@hooks';
const defaultData = {
    objectMoreCount: 0,
    objectOverflow: false,
    objectsToDisplay: [],
    status: '',
    userMoreCount: 0,
    userOverflow: false,
    usersToDisplay: []
};
const detectedStatus = DataSecurityApplicationStatus[DataSecurityApplicationStatus.Detected].toLowerCase();
const hiddenStatus = DataSecurityApplicationStatus[DataSecurityApplicationStatus.Deleted].toLowerCase();
export const DetectedApplicationCard = ({ applicationId }) => {
    var _a, _b;
    const [data, setData] = useState(defaultData);
    const [loading, setLoading] = useState(true);
    const showCounts = useFeatureFlag(FeatureFlag.DataSecurityDashboard);
    const loadData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        const results = yield getDataSecurityDetectedApplicationById({ applicationId });
        if (!results.data) {
            return;
        }
        const userOverflow = results.data.users.length > 3;
        const objectOverflow = results.data.totalObjects > results.data.objects.length;
        setData({
            firstDetected: results.data.firstDetected,
            lastDetected: results.data.lastDetected,
            objectMoreCount: objectOverflow ? results.data.totalObjects - results.data.objects.length + 1 : 0,
            objectOverflow,
            objectsToDisplay: objectOverflow ?
                results.data.objects.slice(0, results.data.objects.length - 1) :
                results.data.objects,
            status: results.data.status,
            userMoreCount: userOverflow ? results.data.users.length - 2 : 0,
            userOverflow,
            usersToDisplay: userOverflow ? results.data.users.slice(0, 2) : results.data.users
        });
        setLoading(false);
    }), [applicationId, setLoading]);
    useOnMount(() => {
        loadData();
    });
    const handleStatusChange = useCallback((status) => {
        setData((current) => (Object.assign(Object.assign({}, current), { status })));
    }, []);
    return (React.createElement("div", { className: classNames('detected-app-card', { 'hidden-styling': data.status === hiddenStatus }), "data-cy": `detected-app-card-${applicationId}` },
        React.createElement("div", { className: 'detected-app-header' },
            React.createElement(Row, { align: 'middle', className: 'header-first-row', justify: 'space-between' },
                React.createElement(Col, null,
                    React.createElement(If, { condition: data.status === detectedStatus },
                        React.createElement(Then, null,
                            React.createElement(WarningTwoTone, { style: { fontSize: '20px' }, twoToneColor: '#FAAD14' })),
                        React.createElement(Else, null,
                            React.createElement(EyeInvisibleOutlined, { style: { fontSize: '20px' } })))),
                React.createElement(Col, null,
                    React.createElement(FullUserBoundary, null,
                        React.createElement(DetectedApplicationActions, { applicationId: applicationId, onStatusChange: handleStatusChange, status: data.status })))),
            React.createElement(Row, { justify: 'center' },
                React.createElement(AppDisplay, { appId: applicationId }))),
        React.createElement("div", { className: 'detected-app-body' },
            React.createElement(LoadingSpinner, { spinning: loading },
                React.createElement(Row, { className: 'flex-column' },
                    React.createElement(SonarText, { weight: 'semibold' },
                        " ",
                        COPY_SPECIFIC.USERS,
                        " "), (_a = data.usersToDisplay) === null || _a === void 0 ? void 0 :
                    _a.map((email) => React.createElement(Tooltip, { key: email, mouseEnterDelay: 0.5, title: email },
                        React.createElement(SonarText, { className: 'user', key: email },
                            " ",
                            email))),
                    React.createElement(When, { condition: data.userOverflow },
                        React.createElement(SonarText, null, DATA_SECURITY_DASHBOARD.PLUS_MORE(data.userMoreCount)))),
                React.createElement(Row, { align: 'middle', justify: 'space-between' },
                    React.createElement(Col, { className: 'flex-column' },
                        React.createElement(SonarText, { weight: 'semibold' }, DATA_SECURITY_DASHBOARD.FIRST_DETECTED),
                        React.createElement(SonarText, null, dayjs(data === null || data === void 0 ? void 0 : data.firstDetected).format('MMM Do, YYYY'))),
                    React.createElement(Col, { className: 'flex-column' },
                        React.createElement(SonarText, { weight: 'semibold' }, DATA_SECURITY_DASHBOARD.LAST_DETECTED),
                        React.createElement(SonarText, null, dayjs(data === null || data === void 0 ? void 0 : data.lastDetected).format('MMM Do, YYYY')))),
                React.createElement(When, { condition: showCounts },
                    React.createElement(Row, { className: 'flex-column' },
                        React.createElement(Row, { align: 'middle', justify: 'space-between' },
                            React.createElement(Col, null,
                                React.createElement(SonarText, { weight: 'semibold' }, COPY_SPECIFIC.OBJECTS)),
                            React.createElement(Col, null,
                                React.createElement(SonarText, { weight: 'semibold' }, COPY_SPECIFIC.FIELD_COUNT))), (_b = data.objectsToDisplay) === null || _b === void 0 ? void 0 :
                        _b.map((object) => React.createElement(Row, { align: 'middle', justify: 'space-between', key: object.name },
                            React.createElement(Col, null, object.name),
                            React.createElement(Col, null, object.count))),
                        React.createElement(When, { condition: data.objectOverflow },
                            React.createElement(SonarText, null, DATA_SECURITY_DASHBOARD.PLUS_MORE(data.objectMoreCount)))))))));
};
